import React, { useEffect, useMemo, useState } from "react";
import { PlusCircle, AlertTriangle, Pencil } from "lucide-react";
import { timeclockApi, locationLogsApi, payrollApi } from "../../../lib/api";

const QUICK_ACTIONS = [
  { key: "missing", label: "Add Missing Hours", focusField: "regularHours" },
  { key: "correct", label: "Correct Hours", focusField: "regularHours" },
  { key: "pto", label: "Add PTO", focusField: "ptoHours" },
  { key: "sick", label: "Add Sick Time", focusField: "sickHours" },
  { key: "holiday", label: "Add Holiday", focusField: "holidayHours" },
  { key: "remove", label: "Remove Hours", focusField: "regularHours" },
];

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
function fmtDate(d) {
  return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

/** Builds one row per calendar day that has ANY recorded activity (time
 * clock, job-site GPS event, or an existing dated adjustment) — days with
 * nothing on file are skipped rather than fabricated. */
function buildDayRows(entries, locationEvents, adjustments) {
  const days = new Set();
  entries.forEach((e) => days.add((e.clockIn || "").slice(0, 10)));
  locationEvents.forEach((e) => days.add((e.at || "").slice(0, 10)));
  (adjustments || []).filter((a) => a.date && ["TIME", "PTO", "SICK", "HOLIDAY"].includes(a.category)).forEach((a) => days.add(a.date));
  days.delete("");

  return Array.from(days).sort().reverse().map((day) => {
    const dayEntries = entries.filter((e) => (e.clockIn || "").slice(0, 10) === day);
    const dayEvents = locationEvents.filter((e) => (e.at || "").slice(0, 10) === day);
    const dayAdjustments = (adjustments || []).filter((a) => a.date === day && ["TIME", "PTO", "SICK", "HOLIDAY"].includes(a.category));

    const timeClockHours = dayEntries.reduce((s, e) => s + (e.paidSeconds || 0) / 3600, 0);
    const onSite = dayEvents.filter((e) => e.event === "on_site").sort((a, b) => a.at.localeCompare(b.at))[0];
    const departure = dayEvents.filter((e) => e.event === "complete").sort((a, b) => b.at.localeCompare(a.at))[0]
      || dayEvents.filter((e) => e.event === "start_work").sort((a, b) => b.at.localeCompare(a.at))[0];
    const jobSiteHours = onSite && departure && departure.at > onSite.at ? (new Date(departure.at) - new Date(onSite.at)) / 3600000 : null;
    const gpsAvailable = dayEvents.some((e) => e.techCoords);

    const warnings = [];
    if (dayEvents.length > 0 && dayEntries.length === 0) warnings.push("Missing clock-in");
    if (dayEntries.some((e) => !e.clockOut)) warnings.push("Missing clock-out");
    if (dayEntries.length > 1) warnings.push("Double clock-in");
    const sorted = [...dayEntries].filter((e) => e.clockOut).sort((a, b) => a.clockIn.localeCompare(b.clockIn));
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].clockOut > sorted[i + 1].clockIn) { warnings.push("Overlapping shifts"); break; }
    }
    if (timeClockHours > 12) warnings.push("Unusually long shift");
    if (timeClockHours > 0 && timeClockHours < 1) warnings.push("Unusually short shift");
    if (jobSiteHours != null && timeClockHours > 0 && Math.abs(jobSiteHours - timeClockHours) > 1) warnings.push("GPS/time mismatch");
    if (dayAdjustments.length > 0 && dayEntries.length === 0) warnings.push("Manual time entry");
    if (dayAdjustments.some((a) => a.category === "TIME") && dayEntries.length > 0) warnings.push("Edited time");
    if (timeClockHours > 8) warnings.push("Possible OT (>8h/day)");

    return { day, dayEntries, timeClockHours, onSite, departure, jobSiteHours, gpsAvailable, dayAdjustments, warnings };
  });
}

export default function TimeTab({ row, period, adjustments, canPayroll, onOpenTimeModal }) {
  const [entries, setEntries] = useState([]);
  const [locationEvents, setLocationEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balances, setBalances] = useState(null);

  useEffect(() => {
    let cancelled = false;
    payrollApi.getBalances(row.tech.id).then((b) => { if (!cancelled) setBalances(b); }).catch(() => { if (!cancelled) setBalances(null); });
    return () => { cancelled = true; };
  }, [row.tech.id]);

  useEffect(() => {
    if (!period?.startDate || !period?.endDate) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      timeclockApi.entries(period.startDate, period.endDate, row.tech.id).catch(() => []),
      locationLogsApi.list({ techId: row.tech.id, from: period.startDate, to: period.endDate }).catch(() => []),
    ]).then(([e, l]) => {
      if (cancelled) return;
      setEntries(e || []);
      setLocationEvents(l || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [row.tech.id, period?.startDate, period?.endDate]);

  const dayRows = useMemo(() => buildDayRows(entries, locationEvents, adjustments), [entries, locationEvents, adjustments]);
  const canEditTime = canPayroll("edit_hours") || canPayroll("add_adjustments");

  return (
    <div data-testid="payroll-time-tab">
      {balances && (
        <div className="flex flex-wrap gap-3 mb-3" data-testid="time-off-balances">
          <div className="flex-1 min-w-[220px] rounded-lg border border-slate-200 px-3 py-2 bg-slate-50/60">
            <div className="text-[10px] font-bold uppercase text-slate-400">PTO Balance</div>
            <div className="text-[12px] text-slate-700 mt-0.5">
              <span className="font-semibold">{balances.ptoAvailableHours.toFixed(2)}</span> available ·{" "}
              <span className="font-semibold">{balances.ptoUsedHours.toFixed(2)}</span> used ·{" "}
              <span className={`font-semibold ${balances.ptoRemainingHours < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="pto-remaining">{balances.ptoRemainingHours.toFixed(2)}</span> remaining
            </div>
          </div>
          <div className="flex-1 min-w-[220px] rounded-lg border border-slate-200 px-3 py-2 bg-slate-50/60">
            <div className="text-[10px] font-bold uppercase text-slate-400">Sick Balance</div>
            <div className="text-[12px] text-slate-700 mt-0.5">
              <span className="font-semibold">{balances.sickAvailableHours.toFixed(2)}</span> available ·{" "}
              <span className="font-semibold">{balances.sickUsedHours.toFixed(2)}</span> used ·{" "}
              <span className={`font-semibold ${balances.sickRemainingHours < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="sick-remaining">{balances.sickRemainingHours.toFixed(2)}</span> remaining
            </div>
          </div>
        </div>
      )}
      {canEditTime && (
        <div className="flex flex-wrap gap-2 mb-4">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.key} onClick={() => onOpenTimeModal({ focusField: a.focusField })}
              data-testid={`time-quick-action-${a.key}`}
              className="h-8 px-2.5 rounded-md border border-slate-200 text-[11.5px] font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-1"
            >
              <PlusCircle className="h-3.5 w-3.5" /> {a.label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center text-slate-400 italic py-8">Loading time records…</div>
      ) : dayRows.length === 0 ? (
        <div className="text-center text-slate-400 italic py-8">No time-clock, GPS, or manual records found for this period.</div>
      ) : (
        <div className="space-y-3">
          {dayRows.map((d) => (
            <div key={d.day} className="rounded-lg border border-slate-200 p-3" data-testid={`time-day-row-${d.day}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[13px] font-bold text-slate-800">{fmtDate(d.day)}</div>
                {canEditTime && (
                  <button
                    onClick={() => onOpenTimeModal({ defaultDate: d.day, focusField: "regularHours" })}
                    data-testid={`time-day-correct-${d.day}`}
                    className="h-6 px-2 rounded border border-slate-200 text-[10.5px] font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-1"
                  >
                    <Pencil className="h-2.5 w-2.5" /> Correct
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
                <div>
                  <div className="text-[10px] font-bold uppercase text-slate-400">Time Clock</div>
                  {d.dayEntries.length > 0 ? (
                    <div className="text-slate-700">
                      {d.dayEntries.map((e, i) => (
                        <div key={i}>{fmtTime(e.clockIn)} – {e.clockOut ? fmtTime(e.clockOut) : <span className="text-amber-600">open</span>}</div>
                      ))}
                      <div className="font-semibold">{d.timeClockHours.toFixed(2)} hrs</div>
                    </div>
                  ) : <span className="text-slate-300">None</span>}
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase text-slate-400">Job Site</div>
                  {d.onSite ? (
                    <div className="text-slate-700">
                      {fmtTime(d.onSite.at)} – {d.departure ? fmtTime(d.departure.at) : <span className="text-slate-400">—</span>}
                      {d.jobSiteHours != null && <div className="font-semibold">{d.jobSiteHours.toFixed(2)} hrs</div>}
                    </div>
                  ) : <span className="text-slate-300">None</span>}
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase text-slate-400">GPS</div>
                  <span className={d.gpsAvailable ? "text-emerald-600 font-semibold" : "text-slate-300"}>{d.gpsAvailable ? "Available" : "None"}</span>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase text-slate-400">Manual</div>
                  {d.dayAdjustments.length > 0 ? (
                    <div className="text-slate-700">
                      {d.dayAdjustments.map((a) => (
                        <div key={a.id}>{a.category} {a.adjustmentAmount >= 0 ? "+" : ""}{a.adjustmentAmount}</div>
                      ))}
                    </div>
                  ) : <span className="text-slate-300">None</span>}
                </div>
              </div>
              {d.warnings.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2.5 pt-2 border-t border-slate-100">
                  {d.warnings.map((w) => (
                    <span key={w} className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                      <AlertTriangle className="h-2.5 w-2.5" /> {w}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 text-[11px] text-slate-400 italic">
        Warnings are informational only — they do not block payroll unless configured as a blocking exception in a later phase.
      </div>
    </div>
  );
}
